import { useMutation, useQuery } from '@apollo/client';

import { Footer, TitleBar } from '../../../../components';
import QueryErrorBoundary from '../../../../components/error-handling';
import TableV3 from '../../../../components/generic/Table/TableV3';

import {
  CREATE_BUSINESS_FUNCTION,
  DELETE_BUSINESS_FUNCTION,
  GET_BUSINESS_FUNCTIONS,
  UPDATE_BUSINESS_FUNCTION,
} from './queries';

import { formFields } from './formFields';
import { BusinessFunction } from './types';
import { businessFunctionColumns } from './columns';
import { templateColumnConfigs, templateUploadConfig } from './template';
import { baseColumns } from './export';
import TitleUpdater from '../../../../components/common/TitleUpdater';
import { useUserContext } from '../../../../context/PermissionsContext';

export type BusinessFunctionsData = {
  getBusinessFunctions: BusinessFunction[];
};

const BusinessFunctions = () => {
  const REFETCH_QUERY = 'GetBusinessFunctions';

  const { canEdit } = useUserContext();

  const { data, loading, error } = useQuery<BusinessFunctionsData>(
    GET_BUSINESS_FUNCTIONS,
  );

  const [createBusinessFunction, { loading: createLoading }] = useMutation(
    CREATE_BUSINESS_FUNCTION,
    {
      refetchQueries: [REFETCH_QUERY],
    },
  );

  const [updatBusinessFunction, { loading: updateLoading }] = useMutation(
    UPDATE_BUSINESS_FUNCTION,
    {
      refetchQueries: [REFETCH_QUERY],
    },
  );

  const [deleteBusinessFunction, { loading: deleteLoading }] = useMutation(
    DELETE_BUSINESS_FUNCTION,
    {
      refetchQueries: [REFETCH_QUERY],
    },
  );

  const businessFunctionsData = data?.getBusinessFunctions || [];

  const loadingStates =
    loading || createLoading || updateLoading || deleteLoading;

  return (
    <>
      <TitleUpdater title="Business Functions" />
      <TitleBar
        title="Data Inputs"
        subtitle="Enterprise Business Functions"
        brief="Add major Business Functions in the table below, which will allow you to tag applications to them in MEO. Doing so will allow you to create filter reports to show how your various functions depend on IT applications across the organization."
      />
      <QueryErrorBoundary error={error}>
        <TableV3
          title="Business Functions"
          columns={businessFunctionColumns}
          data={businessFunctionsData}
          loading={loadingStates}
          editable
          editableOptions={{
            create: canEdit,
            update: canEdit,
            delete: canEdit,
          }}
          formFields={formFields}
          createGQL={createBusinessFunction}
          updateGQL={updatBusinessFunction}
          deleteGQL={deleteBusinessFunction}
          templateUploadConfig={templateUploadConfig}
          templateColumns={templateColumnConfigs}
          exportExcelColumns={baseColumns}
          inlineEditing
        />
      </QueryErrorBoundary>
      <Footer backPath="/data-inputs" />
    </>
  );
};

export default BusinessFunctions;
